/**
 * `snapshotRotationConversion.ts` — the three.js-typed adapter for the
 * NODE-FRAME half of the Unity ↔ three basis change.
 *
 * All the math lives in `@models/utils/unityBasis`; this file only puts
 * `Vector3` / `Quaternion` types on it, because `src/models/**` may not import
 * three (`Campfire_Web_General` §4). Do not re-derive a conversion here.
 *
 * ## What "node frame" means, and when NOT to use this file
 *
 * An entity snapshot stores a Unity LOCAL TRS, applied verbatim by the desktop
 * client (`ProjectModelSlim.cs:1523-1535`). The three-side node it addresses is
 * the glTF node the same asset exports to, and glTFast relates those two frames
 * by exactly `M = diag(-1, 1, 1)` — so a snapshot converts by the plain basis
 * map, with no forward-convention adapter.
 *
 * For a WORLD pose whose local −Z is treated as "forward" — cameras,
 * participants, cards, markers — use `participantTransformConversion.ts`
 * instead. Picking the wrong one of the two turns an object 180° around, which
 * is exactly the failure this split exists to make hard.
 *
 * ## History (task 0004)
 *
 * This file's map was always correct. The mirror came from
 * `sceneEntityRenderer`'s `basisRoot`, which multiplied an extra
 * `diag(1, 1, -1)` on top and made the composite `diag(-1, 1, -1)` — det +1, a
 * rotation, which cannot carry left-handed content into a right-handed
 * renderer. The per-axis flag table that used to live here (`flipDirection`
 * booleans per axis) is gone: it presented a fixed mathematical fact as
 * configuration, which is how the map came to be re-derived, differently, in
 * eight places.
 */

import { Quaternion, Vector3 } from "three";
import type { Vec3 as EntityVec3 } from "@models/data/entityData";
import {
  convertBasisEulerDegrees,
  convertBasisPosition,
  convertBasisQuaternion,
  unityEulerToQuat,
  unityQuatToEuler,
} from "@models/utils/unityBasis";

/** Snapshot Unity ZXY euler degrees → the three-space local quaternion. */
export function unityEulerToThreeQuat(v: EntityVec3): Quaternion {
  const [x, y, z, w] = unityEulerToQuat(convertBasisEulerDegrees(v));
  return new Quaternion(x, y, z, w).normalize();
}

/** Snapshot Unity local position → three-space local position. */
export function snapshotPositionToThreeVec3(v: EntityVec3, scale = 1): Vector3 {
  const [x, y, z] = convertBasisPosition(v, scale);
  return new Vector3(x, y, z);
}

/** Three-space local position → the value to persist on a snapshot. */
export function threePositionToSnapshotPosition(
  v: Vector3,
  scale = 1,
): { x: number; y: number; z: number } {
  const [x, y, z] = convertBasisPosition([v.x, v.y, v.z], scale);
  return { x, y, z };
}

/**
 * Three-space local rotation → the Unity ZXY euler degrees to persist.
 *
 * Inverse of {@link unityEulerToThreeQuat}. The range is `[-180, 180]`, NOT
 * Unity's `[0, 360)` — a deliberate, pre-existing divergence kept because
 * `PlacementView` renders the stored number verbatim, so switching would turn
 * every -10° part into 350° in the inspector. Unity itself cannot tell the two
 * apart (`Quaternion.Euler` is periodic mod 360). See `DegreeRange`.
 */
export function threeQuatToUnityEuler(q: Quaternion): { x: number; y: number; z: number } {
  const converted = convertBasisQuaternion([q.x, q.y, q.z, q.w]);
  const [x, y, z] = unityQuatToEuler(converted, "signed");
  return { x, y, z };
}
